import React, { useState, useEffect } from 'react';
import { Coupon } from '../core/_models';
import { createCoupon, updateCoupon } from '../core/subscriptionService';
import { useToast } from '../../components/Toats';

interface Props {
  show: boolean;
  handleClose: () => void;
  coupon?: Coupon | null;
  onSaved?: () => void;
}

export const CouponFormModal: React.FC<Props> = ({ show, handleClose, coupon, onSaved }) => {
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'PERCENTAGE' | 'FIXED'>('PERCENTAGE');
  const [discountValue, setDiscountValue] = useState<number>(0);
  const [durationMode, setDurationMode] = useState<'FIRST_ONLY' | 'ALL_CHARGES'>('ALL_CHARGES');
  const [maxUsage, setMaxUsage] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const { toggleToast } = useToast();

  useEffect(() => {
    if (coupon) {
      setCode(coupon.code);
      setDiscountType(coupon.discount_type);
      setDiscountValue(coupon.discount_value);
      setDurationMode(coupon.duration_mode || 'ALL_CHARGES');
      setMaxUsage(coupon.max_usage ? String(coupon.max_usage) : '');
      setExpiresAt(coupon.expires_at ? coupon.expires_at.substring(0, 10) : '');
    } else {
      setCode('');
      setDiscountType('PERCENTAGE');
      setDiscountValue(0);
      setDurationMode('ALL_CHARGES');
      setMaxUsage('');
      setExpiresAt('');
    }
  }, [coupon]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        code,
        discount_type: discountType,
        discount_value: Number(discountValue),
        duration_mode: durationMode,
        max_usage: maxUsage ? Number(maxUsage) : null,
        expires_at: expiresAt || null
      };

      if (coupon) {
        await updateCoupon(coupon.id, payload);
        toggleToast('Cupom atualizado com sucesso.', 'success');
      } else {
        await createCoupon(payload);
        toggleToast('Cupom criado com sucesso.', 'success');
      }
      if (onSaved) onSaved();
      handleClose();
    } catch (error: any) {
      console.error(error);
      toggleToast(error?.response?.data?.message || error?.response?.data?.error || 'Erro ao salvar cupom.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className='modal fade show d-block' tabIndex={-1} aria-modal='true' role='dialog'>
        <div className='modal-dialog modal-dialog-centered modal-lg'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h2 className='fw-bolder'>{coupon ? 'Editar Cupom' : 'Novo Cupom de Desconto'}</h2>
              <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
                <i className='bi bi-x fs-1'></i>
              </div>
            </div>

            <div className='modal-body py-8 px-lg-10'>
              <div className='row g-6'>
                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800 required'>Código do Cupom</label>
                  <input
                    type='text'
                    className='form-control form-control-solid'
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    placeholder='Ex: SOLAR10'
                  />
                  <span className='text-muted fs-7 mt-1 d-block'>O código será exibido ao cliente no checkout.</span>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800 required'>Tipo de Desconto</label>
                  <div className='d-flex gap-4 mt-2'>
                    <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary flex-grow-1 py-4 ${discountType === 'PERCENTAGE' ? 'active border-primary' : ''}`}>
                      <input className='btn-check' type='radio' name='discountType' checked={discountType === 'PERCENTAGE'} onChange={() => setDiscountType('PERCENTAGE')} />
                      <i className='bi bi-percent fs-3 me-2'></i>
                      Percentual
                    </label>
                    <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary flex-grow-1 py-4 ${discountType === 'FIXED' ? 'active border-primary' : ''}`}>
                      <input className='btn-check' type='radio' name='discountType' checked={discountType === 'FIXED'} onChange={() => setDiscountType('FIXED')} />
                      <i className='bi bi-cash fs-3 me-2'></i>
                      Valor Fixo
                    </label>
                  </div>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800 required'>Valor do Desconto</label>
                  <div className='input-group input-group-solid'>
                    <span className='input-group-text'>{discountType === 'PERCENTAGE' ? '%' : 'R$'}</span>
                    <input
                      type='number'
                      className='form-control form-control-solid'
                      value={discountValue}
                      onChange={e => setDiscountValue(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800 required'>Aplicação do Desconto</label>
                  <div className='d-flex gap-4 mt-2'>
                    <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary flex-grow-1 py-4 ${durationMode === 'FIRST_ONLY' ? 'active border-primary' : ''}`}>
                      <input className='btn-check' type='radio' name='durationMode' checked={durationMode === 'FIRST_ONLY'} onChange={() => setDurationMode('FIRST_ONLY')} />
                      <i className='bi bi-1-circle fs-3 me-2'></i>
                      Apenas 1ª parcela
                    </label>
                    <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary flex-grow-1 py-4 ${durationMode === 'ALL_CHARGES' ? 'active border-primary' : ''}`}>
                      <input className='btn-check' type='radio' name='durationMode' checked={durationMode === 'ALL_CHARGES'} onChange={() => setDurationMode('ALL_CHARGES')} />
                      <i className='bi bi-arrow-repeat fs-3 me-2'></i>
                      Todas as parcelas
                    </label>
                  </div>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800'>Limite de Usos</label>
                  <input
                    type='number'
                    className='form-control form-control-solid'
                    value={maxUsage}
                    onChange={e => setMaxUsage(e.target.value)}
                    placeholder='Ilimitado'
                  />
                  <span className='text-muted fs-7 mt-1 d-block'>Deixe vazio para uso ilimitado.</span>
                </div>

                <div className='col-md-6'>
                  <label className='form-label fw-bolder text-gray-800'>Data de Expiração</label>
                  <input
                    type='date'
                    className='form-control form-control-solid'
                    value={expiresAt}
                    onChange={e => setExpiresAt(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <span className='text-muted fs-7 mt-1 d-block'>Deixe vazio para não expirar.</span>
                </div>
              </div>

              {code && discountValue > 0 && (
                <div className='border border-dashed border-gray-300 rounded p-6 mt-8'>
                  <h4 className='fw-bolder mb-4'>Resumo</h4>
                  <div className='d-flex align-items-center gap-3'>
                    <span className='badge badge-light-warning fw-bolder fs-5 px-4 py-3'>{code}</span>
                    <span className='text-gray-600 fw-bold fs-5'>
                      →  Desconto de{' '}
                      <span className='text-gray-800 fw-bolder'>
                        {discountType === 'PERCENTAGE' ? `${discountValue}%` : `R$ ${discountValue.toFixed(2).replace('.', ',')}`}
                      </span>
                      {durationMode === 'FIRST_ONLY' ? ', apenas na 1ª parcela' : ', em todas as parcelas'}
                      {maxUsage ? `, limitado a ${maxUsage} usos` : ', sem limite de usos'}
                      {expiresAt ? `, expira em ${new Date(expiresAt + 'T00:00:00').toLocaleDateString('pt-BR')}` : ', sem expiração'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className='modal-footer'>
              <button type='button' className='btn btn-light fw-bolder' onClick={handleClose} disabled={saving}>
                Cancelar
              </button>
              <button type='button' className='btn btn-primary fw-bolder' onClick={handleSave} disabled={saving}>
                {saving ? 'Salvando...' : coupon ? 'Atualizar Cupom' : 'Criar Cupom'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  );
};
